import React, { useEffect, useState } from 'react';
import { ID } from '../lib/appwrite';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Home.css';
import Modal from '../components/Modal';
import LoadingOverlay from '../components/LoadingOverlay';
import { api } from '../services/db';
import { sortReportItems } from '../utils/helpers';

export default function Home() {
  const { user, logout, warehouseId, warehouseName, teamRoles, allTeams, resetWarehouseSelection } = useAuth();
  const navigate = useNavigate();
  const [type, setType] = useState('وارد');
  const [date] = useState(() => {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  });

  // We'll load items from DB
  const [dbItems, setDbItems] = useState([]);
  const [items, setItems] = useState([{ name: '', qty: '' }]);

  const [incomingCert, setIncomingCert] = useState('');
  const [receiver, setReceiver] = useState('');
  const [receiptStatus, setReceiptStatus] = useState('كامل');
  // Removed unused remainingList
  const [loading, setLoading] = useState(true);

  // Modal State
  // Modal State
  // Modal State
  const [modal, setModal] = useState({ isOpen: false, message: '', type: 'success' });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    // Guard: Production users redirect to /production
    if (teamRoles.includes('production')) {
      navigate('/production');
      return;
    }

    if (warehouseId) loadItems();
    // eslint-disable-next-line
  }, [warehouseId, teamRoles]);

  async function loadItems() {
    setLoading(true);
    const data = await api.getItems(warehouseId);
    const sorted = sortReportItems(data);
    setDbItems(sorted);
    setLoading(false);
  }

  useEffect(() => {
    if (type !== 'منصرف') {
      setReceiver('');
      setReceiptStatus('كامل');
    }
  }, [type]);

  function addItemRow() {
    setItems(prev => [...prev, { name: '', qty: '', received: '', remaining: '' }]);
  }

  function removeItemRow(idx) {
    setItems(prev => prev.filter((_, i) => i !== idx));
  }

  function updateItem(idx, field, value) {
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;

      const newItem = { ...it, [field]: value };

      // Auto-calc logic
      if (field === 'qty' || field === 'received') {
        const q = parseInt(newItem.qty) || 0;
        let r = 0;

        if (field === 'received') {
          if (value === '') {
            newItem.received = '';
            r = 0;
          } else {
            r = parseInt(value) || 0;
            if (r > q) r = q; // Cap at max
            if (r < 0) r = 0;
            newItem.received = r; // Update with clamped value
          }
        } else {
          // field is qty
          if (newItem.received === '') {
            r = 0;
            return { ...newItem, remaining: q }; // Just reset received to 0 implication or keep as is? Original logic:
          } else {
            r = parseInt(newItem.received) || 0;
            if (r > q) r = q; // Re-clamp existing received
            newItem.received = r;
          }
        }

        newItem.remaining = q - r;
      }

      return newItem;
    }));
  }



  async function addTransaction() {
    // Validations
    if (!date) { setModal({ isOpen: true, message: 'يرجى إدخال التاريخ.', type: 'error' }); return; }

    if (items.some(it => !it.name || it.name === '')) {
      setModal({ isOpen: true, message: 'يرجى تحديد الصنف لجميع الصفوف.', type: 'error' });
      return;
    }

    if (items.some(it => !it.qty || Number(it.qty) <= 0)) {
      setModal({ isOpen: true, message: 'يرجى تحديد الكمية لجميع الصفوف.', type: 'error' });
      return;
    }

    const parsedItems = items
      .map(it => ({
        ...it,
        qty: parseInt(it.qty, 10) || 0,
        received: it.received,
        remaining: it.remaining
      }))
      .filter(it => it.name && it.qty > 0);

    if (parsedItems.length === 0) { setModal({ isOpen: true, message: 'يرجى إدخال صنف واحد على الأقل مع كمية صحيحة.', type: 'error' }); return; }

    if (type === 'منصرف') {
      if (!receiver) { setModal({ isOpen: true, message: 'يرجى إدخال اسم المستلم.', type: 'error' }); return; }
    }
    if (type === 'وارد') {
      if (!incomingCert) { setModal({ isOpen: true, message: 'يرجى إدخال شهادة الوارد أو اسم الجهة.', type: 'error' }); return; }
    }

    setIsProcessing(true);
    await new Promise(resolve => setTimeout(resolve, 100)); // Force render

    await new Promise(resolve => setTimeout(resolve, 100)); // Force render

    // const rollbackStack = []; // To store compensating actions { type: 'deleteTx'|'revertStock', payload: ... }

    try {
      // 1. Fetch Items for Stock Validation
      // We re-fetch to ensure fresh stock data
      const currentDbItems = await api.getItems(warehouseId);

      // PHASE 1: Validation (Read-Only)
      for (const it of parsedItems) {
        const itemDoc = currentDbItems.find(x => x.name === it.name);
        if (!itemDoc) {
          throw new Error(`لم يتم العثور على الصنف: ${it.name}`);
        }
        if (type === 'منصرف') {
          const currentStock = itemDoc.current_stock || 0;
          if (it.qty > currentStock) {
            throw new Error(`لا يمكن صرف ${it.qty} من صنف ${it.name}. الرصيد الحالي هو ${currentStock}.`);
          }
        }
      }

      // PHASE 2: Execution via Function


      // PHASE 3: Execution (Atomic Function)
      const data = {
        items: parsedItems.map(it => ({
          name: it.name,
          qty: it.qty,
          received: it.received,
          remaining: it.remaining
        })),
        type: type,
        date: date,
        warehouse_id: warehouseId,
        user_name: user.name,
        notes: type === 'وارد' ? incomingCert : (receiver || ''),
        incoming_cert: incomingCert,
        receipt_status: receiptStatus,
        receiver: receiver,
        request_id: requestId // Idempotency Key
      };

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('UI_TIMEOUT')), 20000)
      );

      const result = await Promise.race([
        api.executeCreateTransaction(data, warehouseId),
        timeoutPromise
      ]);

      const code = result.code;

      setModal({ isOpen: true, message: `تم حفظ المعاملة بنجاح، رقم الإيصال: ${code}`, type: 'success' });
      resetForm();

    } catch (error) {
      console.error("Transaction Error:", error);
      if (error.message === 'UI_TIMEOUT') {
        setModal({
          isOpen: true,
          message: 'حدث خطاء في الشبكة ، تفقد المعاملات السابقة للتأكد من نجاح العملية او فشلها قبل المحاولة مرة اخرى',
          type: 'warning'
        });
      } else {
        setModal({ isOpen: true, message: `فشلت العملية: ${error.message}`, type: 'error' });
      }
    } finally {
      setIsProcessing(false);
    }
  }


  // Idempotency: Keep a unique ID for the current attempt. Only reset on success.
  const [requestId, setRequestId] = useState(ID.unique());

  function resetForm() {
    loadItems(); // Refresh items to get new stock
    setItems([{ name: '', qty: '', received: '', remaining: '' }]);
    setReceiver('');
    setIncomingCert('');
    // Also reset type and date? Maybe keep them. User didn't specify. Keeping them is usually friendlier.
    setRequestId(ID.unique()); // NEW ID for NEW transaction
  }

  // To properly implement this, I need to update the state structure.
  // Converting handleLogout to just open modal, and adding state to know what to confirm.
  const [pendingAction, setPendingAction] = useState(null); // 'cancelTx' or 'logout'

  function handleCancel() {
    setPendingAction('cancelTx');
    setModal({ isOpen: true, message: 'هل أنت متأكد من إلغاء المعاملة؟', type: 'confirm' });
  }

  function handleLogoutClick() {
    setPendingAction('logout');
    setModal({ isOpen: true, message: 'هل انت متأكد من تسجيل الخروج؟', type: 'confirm' });
  }

  async function onConfirmAction() {
    if (pendingAction === 'cancelTx') {
      resetForm();
    } else if (pendingAction === 'logout') {
      await logout();
      navigate('/login');
    } else if (pendingAction === 'changeWarehouse') {
      resetWarehouseSelection();
      navigate('/choose-warehouse');
    }
    closeModal();
  }

  function closeModal() {
    if (modal.type === 'warning') {
      window.location.reload();
      return;
    }
    setModal({ ...modal, isOpen: false });
    setPendingAction(null);
  }

  // Reload on Back Button if Warning is Open
  useEffect(() => {
    const handlePopState = () => {
      if (modal.isOpen && modal.type === 'warning') {
        window.location.reload();
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [modal]);

  return (
    <div className="home-root" dir="rtl">
      <LoadingOverlay isLoading={isProcessing || loading} />
      <Modal
        isOpen={modal.isOpen}
        message={modal.message}
        type={modal.type}
        onClose={closeModal}
        onConfirm={pendingAction ? onConfirmAction : null}
      />
      <div className="container">
        <div className="desktop-only" style={{ position: 'absolute', top: 20, left: 20, display: 'flex', gap: 10 }}>
          <button id="logout" onClick={handleLogoutClick} style={{ position: 'static' }}>تسجيل الخروج</button>
          {allTeams && allTeams.length > 1 && (
            <button
              onClick={() => {
                setPendingAction('changeWarehouse');
                setModal({ isOpen: true, message: 'هل تريد تغيير المخزن؟', type: 'confirm' });
              }}
              style={{
                background: '#6d6d6dff',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '20px',
                width: 'fit-content',
                height: 'fit-content',
                padding: '10px'
              }}
            >
              تغيير المخزن
            </button>
          )}
        </div>
        <h1 className="brand">{warehouseName || 'نظام إدارة المخازن'}</h1>

        {/* Mobile Hamburger Button */}
        {/* Mobile Hamburger Button */}
        <button className="mobile-menu-btn" onClick={() => setIsSidebarOpen(true)}>
          ☰
        </button>

        {/* Sidebar */}
        <div className={`sidebar ${isSidebarOpen ? 'open' : ''} `}>
          <button className="close-sidebar-btn" onClick={() => setIsSidebarOpen(false)}>✕</button>

          <div className="sidebar-content">
            <Link to="/previous" onClick={() => setIsSidebarOpen(false)}><button className="sidebar-btn">معاملات سابقة</button></Link>
            <Link to="/daily" onClick={() => setIsSidebarOpen(false)}><button className="sidebar-btn">التقرير اليومي</button></Link>
            <Link to="/monthly" onClick={() => setIsSidebarOpen(false)}><button className="sidebar-btn">التقرير الشهري</button></Link>
            {/* <Link to="/inventory" onClick={() => setIsSidebarOpen(false)}><button className="sidebar-btn">المخزون الحالي</button></Link> */}
            {!teamRoles.includes('manager') && !teamRoles.includes('production') && (
              <Link to="/items" onClick={() => setIsSidebarOpen(false)}><button className="sidebar-btn">إدارة الأصناف</button></Link>
            )}
            <Link to="/production" onClick={() => setIsSidebarOpen(false)}><button className="sidebar-btn">إدارة الإنتاج</button></Link>
            <Link to="/sales-home" onClick={() => setIsSidebarOpen(false)}><button className="sidebar-btn">نظام المبيعات</button></Link>
            {teamRoles.includes('owner') && (
              <Link to="/permissions" onClick={() => setIsSidebarOpen(false)}><button className="sidebar-btn">إدارة الصلاحيات</button></Link>
            )}
          </div>

          <div className="sidebar-footer" style={{ flexDirection: 'column', gap: 10 }}>
            {allTeams && allTeams.length > 1 && (
              <button
                onClick={() => {
                  setPendingAction('changeWarehouse');
                  setModal({ isOpen: true, message: 'هل تريد تغيير المخزن؟', type: 'confirm' });
                }}
                style={{
                  background: '#3498db',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  width: '100%',
                  fontSize: '1rem',
                  cursor: 'pointer'
                }}
              >
                تغيير المخزن
              </button>
            )}
            <button id="logout-sidebar" onClick={handleLogoutClick}>تسجيل الخروج</button>
          </div>
        </div>

        {/* Overlay for closing sidebar when clicking outside */}
        {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)}></div>}


        <div className="actions desktop-only">
          <Link to="/previous"><button id="previous-transactions-btn">معاملات سابقة</button></Link>
          <Link to="/daily"><button id="daily-report-btn">التقرير اليومي</button></Link>
          <Link to="/monthly"><button id="monthly-report-btn">التقرير الشهري</button></Link>
          {/* <Link to="/inventory"><button id="inventory-btn">المخزون الحالي</button></Link> */}
          {!teamRoles.includes('manager') && !teamRoles.includes('production') && (
            <Link to="/items"><button id="items-btn">إدارة الأصناف</button></Link>
          )}
          <Link to="/production"><button id="prod-btn">إدارة الإنتاج</button></Link>
          <Link to="/sales-home"><button id="sales-btn">نظام المبيعات</button></Link>
          {teamRoles.includes('owner') && (
            <Link to="/permissions"><button id="permissions-btn">إدارة الصلاحيات</button></Link>
          )}
        </div>

        <div className="input-form">
          <h2>إدخال معاملة (وارد / منصرف)</h2>

          <div className="form-group">
            <label htmlFor="type">نوع الحركة:</label>
            <select id="type" value={type} onChange={e => setType(e.target.value)}>
              <option value="وارد">وارد (إضافة)</option>
              <option value="منصرف">منصرف (خصم)</option>
            </select>
          </div>

          {/* Receiver Field MOVED HERE */}
          {type === 'منصرف' && (
            <div className="form-group">
              <label>اسم المستلم:</label>
              <input type="text" value={receiver} onChange={e => setReceiver(e.target.value)} placeholder="اسم المستلم" />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="date">التاريخ:</label>
            <input id="date" type="date" value={date} readOnly />
          </div>

          <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label className='the-items-label'>الأصناف :</label>
            <div id="items-list">
              {items.map((it, idx) => (
                <div key={idx} className="item-row">
                  <select value={it.name} onChange={e => updateItem(idx, 'name', e.target.value)} style={{ flex: 2 }}>
                    <option value="" disabled>حدد الصنف</option>
                    {dbItems
                      .filter(opt => !items.map(i => i.name).includes(opt.name) || opt.name === it.name)
                      .map(opt => <option key={opt.$id} value={opt.name}>{opt.name}</option>)}
                  </select>
                  <input type="number" min="1" placeholder="الكمية" value={it.qty} onChange={e => updateItem(idx, 'qty', e.target.value)} style={{ flex: 1 }} />

                  {/* Partial Receipt Inputs - Only if Out + Partial */}
                  {type === 'منصرف' && receiptStatus === 'متبقي' && (
                    <>
                      <hr id='mobile-hr' />
                      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                        <label style={{ fontSize: '0.7rem', marginLeft: 2, minWidth: 'auto' }}>مستلم:</label>
                        <input
                          type="number"
                          value={it.received}
                          onChange={e => updateItem(idx, 'received', e.target.value)}
                          onFocus={e => (it.received === 0 || it.received === '0') && updateItem(idx, 'received', '')}
                          onBlur={e => it.received === '' && updateItem(idx, 'received', 0)}
                          style={{ width: 60, background: '#e8f5e9' }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                        <label style={{ fontSize: '0.7rem', marginLeft: 2, minWidth: 'auto' }}>متبقي:</label>
                        <input type="number" value={it.remaining} readOnly disabled style={{ width: 60, background: '#e0e0e0', color: '#555' }} />
                      </div>
                    </>
                  )}

                  <button type="button" className="remove-btn" onClick={() => removeItemRow(idx)}>-</button>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 8, textAlign: 'left' }}>
              <button type="button" id="add-item-btn" onClick={addItemRow}>إضافة صنف</button>
            </div>
          </div>

          {type === 'وارد' && (
            <div id="incoming-fields">
              <div className="form-group">
                <label>شهادة الوارد او اسم الجهة:</label>
                <input type="text" value={incomingCert} onChange={e => setIncomingCert(e.target.value)} placeholder="اسم الجهة أو شهادة الوارد" />
              </div>
              <div className="form-group">
                <label>رقم الإيصال:</label>
                <input type="text" readOnly value="سيتم التوليد تلقائياً" style={{ backgroundColor: '#f0f0f0', color: '#888' }} />
              </div>
            </div>
          )}

          {type === 'منصرف' && (
            <div id="outgoing-fields">
              {/* Receiver was here, moved up */}

              <div className="form-group">
                <label>حالة استلام الإيصال:</label>
                <select value={receiptStatus} onChange={e => setReceiptStatus(e.target.value)}>
                  <option value="كامل">كامل</option>
                  <option value="متبقي">متبقي</option>
                </select>
              </div>

              <div className="form-group">
                <label>رقم الإيصال:</label>
                <input type="text" readOnly value="سيتم التوليد تلقائياً" style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold', color: '#888' }} />
              </div>
            </div>
          )}

          <div className="form-group" style={{ marginTop: 20 }}>
            <button onClick={addTransaction} style={{ flex: 1 }}>حفظ معاملة ({type})</button>
            <button className='cancel-btn' onClick={handleCancel}>إلغاء</button>
          </div>
        </div>
      </div>
    </div>
  );
}
